import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    TextField,
    Button,
    Box,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    useMediaQuery,
    Stack,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GameDialog from '../../widgets/games/GameDialog';
import NewGameDialog from '../../widgets/games/NewGameDialog';
import GameCard from '../../widgets/games/GameCard';
import GameList from '../../widgets/games/GameList';
import GameDeleteDialog from '../../widgets/groups/GameDeleteDialog';
import AdminGameDialog from '../../widgets/admin/AdminGameDialog';
import AdminNewGameDialog from '../../widgets/admin/AdminNewGameDialog';
import AdminGameDeleteDialog from '../../widgets/admin/AdminGameDeleteDialog';

const AdminGamesPage = () => {
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedGame, setSelectedGame] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        RequestService.getCompanies().then(setCompanies);
    }, []);

    const handleCompanyChange = (e) => {
        const companyId = e.target.value;
        setSelectedCompanyId(companyId);
        setGames([]);
    };

    useEffect(() => {
        const fetchGames = async () => {
            if (!selectedCompanyId) return;
            setLoading(true);
            try {
                const data =
                    await RequestService.getGamesAsSuperAdmin(
                        selectedCompanyId
                    );
                setGames(data);
            } catch (err) {
                console.error('Erro ao carregar jogos:', err);
                setError('Erro ao carregar jogos.');
                setGames([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGames();
    }, [selectedCompanyId]);

    const renderCompanySelect = () => (
        <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel>Empresa</InputLabel>
            <Select
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                label="Empresa"
            >
                {companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                        {c.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const handleRowClick = (game) => {
        setSelectedGame(game);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedGame(null);
    };

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleEdit = (game) => {
        handleRowClick(game);
    };

    const handleOpenDeleteDialog = (game) => {
        setSelectedGame(game);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedGame(null);
    };

    const handleSuccessDelete = async () => {
        const data = await RequestService.getGames(selectedCompanyId);
        setGames(data);
        setSnackbarMessage('Jogo excluído com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSuccessAdd = async (message) => {
        const data = await RequestService.getGames(selectedCompanyId);
        setGames(data);
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleSuccessEdit = async (message) => {
        const data = await RequestService.getGames(selectedCompanyId);
        setGames(data);

        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const renderEmptyState = () => (
        <Box
            sx={{
                textAlign: 'center',
                mt: 4,
                color: '#666',
                fontFamily: 'Poppins',
                fontSize: 18,
            }}
        >
            <Typography variant="h6">Nenhum jogo cadastrado.</Typography>
            <Typography variant="body2">
                Clique em <strong>"Novo jogo"</strong> para adicionar o
                primeiro!
            </Typography>
        </Box>
    );

    const renderMobileLayout = () => {
        return games.length === 0 ? (
            renderEmptyState()
        ) : (
            <GameList
                games={games}
                onUserClick={handleRowClick}
                onDeleteClick={handleOpenDeleteDialog}
            />
        );
    };

    const renderDesktopLayout = () => {
        return games.length === 0 ? (
            renderEmptyState()
        ) : (
            <Grid container spacing={3} sx={{ mt: 2 }}>
                {games.map((game, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <GameCard
                            game={game}
                            onEdit={handleEdit}
                            onDelete={() => handleOpenDeleteDialog(game)}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        Início
                    </Link>
                    <Link underline="hover" color="inherit">
                        Definições
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Jogos
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Jogos
                </Typography>
            </Grid>

            <Grid container sx={12} justifyContent={'end'}>
                <Grid item sx={6}>
                    <Button
                        variant="contained"
                        sx={{ minHeight: '47px' }}
                        endIcon={<AddCircleOutlineOutlinedIcon />}
                        onClick={handleOpenAddDialog}
                    >
                        Novo Jogo
                    </Button>
                </Grid>
            </Grid>

            {renderCompanySelect()}

            {loading ? (
                <Stack alignItems="center" mt={4}>
                    <CircularProgress />
                </Stack>
            ) : isMobile ? (
                renderMobileLayout()
            ) : (
                renderDesktopLayout()
            )}

            <AdminGameDialog
                open={isDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDialog();
                    }
                }}
                game={selectedGame}
                onSuccess={handleSuccessEdit}
            />

            <AdminNewGameDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAddDialog();
                    }
                }}
                onSuccess={handleSuccessAdd}
                company={selectedCompanyId}
            />

            <AdminGameDeleteDialog
                open={isDeleteDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDeleteDialog();
                    }
                }}
                game={selectedGame}
                onSuccess={handleSuccessDelete}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminGamesPage;
